import {
  BlockPayload,
  LayoutBlockPayload,
  Notification,
  WorkflowPayload,
} from '@basehub/data/replicache/index'
import * as React from 'react'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { useTreeSlice } from '~/hooks/use-tree-slice'

export const WorkflowNotificationContent = ({
  notification,
}: {
  notification: Extract<Notification, { type: 'workflow-action' }>
}) => {
  const contentSwitch = React.useMemo(() => {
    switch (true) {
      // @ts-ignore
      case notification.payload.type === 'commit':
      case notification.payload.type === 'commit.created':
        return (
          <>
            <p className="text-dark-dim text-sm">
              New commit on {notification.repoName}
            </p>
            <p className="pl-2 border-l-2 border-dark-border-solid line-clamp-3 text-xs text-dark-faint">
              {notification.payload.data.message}
            </p>
          </>
        )
      case isListEvent(notification.payload): {
        const label = getLabelByEventType(notification.payload)
        return (
          <p className="text-dark-dim text-sm capitalize-first">
            {label} in collection "{notification.payload.data.listBlockTitle}".
          </p>
        )
      }
      // @ts-ignore
      case notification.payload.type === 'event':
      case notification.payload.type === 'event-block.created': {
        const label = getLabelByEventType(notification.payload)
        return (
          <p className="text-dark-dim text-sm capitalize-first">
            {label} in "{notification.payload.data.eventBlockTitle}" block.
          </p>
        )
        return
      }
      default: {
        return <UpdatedBlockNotification payload={notification.payload} />
      }
    }
  }, [notification.payload, notification.repoName])

  return contentSwitch
}

const UpdatedBlockNotification = ({
  payload,
}: {
  payload: LayoutBlockPayload | BlockPayload
}) => {
  const tree = useWorkingTreeStore((s) => s.tree)
  const block = useTreeSlice(tree, (s) => {
    const id =
      'updatedBlockId' in payload.data
        ? // @ts-expect-error
          payload.data.updatedBlockId || payload.data.updatedBlock.id
        : payload.data.blockId
    const block = s.getNode(id)

    return block
  })

  let message = 'A block (deleted) was updated.'
  if (block) {
    message = `Block "${block.value.title || 'Untitled'}" was updated.`
  } else {
    message = 'A deleted block was updated.'
  }

  return <p className="text-dark-dim text-sm capitalize-first">{message}</p>
}

function isListEvent(
  payload: WorkflowPayload
): payload is Extract<
  WorkflowPayload,
  { type: 'list-block.created' | 'list-block.updated' | 'list-block.deleted' }
> {
  return payload.type.startsWith('list-block')
}

/** @example "3 events have been created" */
function getLabelByEventType(
  payload: Exclude<WorkflowPayload, { type: 'commit' }>
) {
  const [primaryType, secondaryType] = payload.type.split('.') as [
    'list-block' | 'event-block' | 'event',
    'created' | 'updated' | 'deleted' | undefined,
  ]

  let label
  const block = primaryType === 'list-block' ? 'row' : 'event'

  if (payload.count && payload.count > 1) {
    label = `${payload.count} ${block}s have`
  } else {
    label = `${block.startsWith('e') ? 'An' : 'A'} ${block} has`
  }

  return `${label} been ${secondaryType ?? 'created'}`
}
